import React from 'react';
import styled from 'styled-components';
import { closeModal, openModal } from '../ducks';
import { connect } from 'react-redux';
import LicenceSelectorScreenV2Content from '../../../components/software_licence/licence_selector_screen_v2_content';
import { getKnowMoreLinks, getLicenceCategories } from '../../screens/licence_selector_screen_v2/ducks';
import Button from '../../../atoms/button';
import I10n from '../../i10n';
import { PRIMARY_COLOR, SECONDARY_COLOR } from '../../../color-constants';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
  padding: 0px 40px;
  flex: 1 1 0px;
  max-width: 100%;
  box-sizing: border-box;
  overflow-y: auto;

  @media (max-width: 800px) {
    padding: 0px 16px;
  }
`;

const TitleAndModelWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  padding: 20px 40px 0px 40px;

  @media (max-width: 800px) {
    padding: 10px 16px 0px 16px;
  }
`;

const LicenseModelWrapper = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  @media (max-width: 800px) {
    justify-content: center;
  }
`;

const LicenseModelButtonsWrapper = styled.div`
  display: flex;
  margin-top: 10px;

  @media (max-width: 800px) {
    display: none;
  }
`;

const MobileLicenseModelButtonsWrapper = styled.div`
  display: none;
  margin-top: 10px;

  @media (max-width: 800px) {
    display: flex;
  }
`;

const LicenseModelButtonWrapper = styled.div`
  :not(:first-of-type) {
    --qds-control-border-radius-top-left: 0px;
    --qds-control-border-radius-bottom-left: 0px;
  }

  :not(:last-of-type) {
    --qds-control-border-radius-top-right: 0px;
    --qds-control-border-radius-bottom-right: 0px;
  }
`;

const StepTitle = styled.span`
  color: black;
  font-size: 20px;
  font-weight: 700;
`;

const StepSubtitle = styled.span`
  color: #333333;
  font-size: 16px;
  font-weight: 400;
  margin-top: 5px;

  @media (max-width: 800px) {
    display: none;
  }
`;

const DisplayGroupTitlesWrapper = styled.div`
  overflow: auto;
  box-shadow: 0px 2px 4px 0px #00000005;
  box-shadow: 0px 1px 6px -1px #00000005;
  box-shadow: 0px 1px 2px 0px #00000008;
`;

const DesktopDisplayGroupTitlesWrapper = styled(DisplayGroupTitlesWrapper)`
  display: flex;
  @media (max-width: 800px) {
    display: none;
  }
`;

const MobileDisplayGroupTitlesWrapper = styled(DisplayGroupTitlesWrapper)`
  display: none;
  @media (max-width: 800px) {
    display: flex;
  }
`;

const DisplayGroupItem = styled.div`
  display: flex;
  flex-direction: column;
  cursor: pointer;
`;

const DisplayGroupLabel = styled.div`
  font-size: 16px;
  font-weight: 400;
  padding: 10px;
  color: #000000;

  ${props => props.$selected ? 'font-weight: 700;' : ''}

`;

const Link = styled.span`
  color: ${PRIMARY_COLOR};
  color: var(--se-secondary, ${PRIMARY_COLOR});
  font-size: 16px;
  cursor: pointer;
  margin-left: 10px;
  :hover {
    text-decoration: underline;
  }
  margin-top: 10px;

  @media (max-width: 800px) {
    display: none;
  }
`;

const SelectionBar = styled.div`
  display: flex;
  background-color: ${SECONDARY_COLOR};
  background-color: var(--se-primary, ${SECONDARY_COLOR});
  border-radius: 8px;

  visibility: hidden;
  height: 5px;

  ${props => props.$selected ? 'visibility: visible;' : ''}
`;

const NavigationButtonSection = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  padding: 10px 5px;
  border-top: 2px solid #EDEDED;
  background-color: white;
`;

const CancelWrapper = styled.div`
  margin-right: 10px;
`;

class LicenceSelectorScreenV2 extends React.Component {

  constructor(props) {
    super(props);
    this.state = {
      licenseDisplayGroups: [],
      scopedLicences: {},
      availableLicenseModel: [],
      licencesLoading: false,
      selectedDisplayGroup: null,
      showPackageSummary: false,
      mobileSelectedLicenseDisplayGroup: null,
      knowMoreLinksMap: {}
    };
  }

  processLicenseSubGroupsWithCompatibilities = (subGroups, forbiddenLicenseSelectors) => {
    const newSubGroups = [];
    for (const subGroup of subGroups) {
      subGroup.comparatorTypes = subGroup.comparatorTypes.filter((type) => {
        return !forbiddenLicenseSelectors.includes(type.comparatorTypeKey);
      });
      if (subGroup.comparatorTypes.length > 0) {
        newSubGroups.push(subGroup);
      }
    }

    return subGroups;
  }

  processLicenseGroupsWithCompatibilites = (licenseDisplayGroups) => {

    const forbiddenLicenseSelectors = this.props.hardwareToSoftwareCompatibilities.flatMap(compatibility => {
      return compatibility.forbiddenLicenseSelectorKeys;
    });

    const newLicenseGroups = [];
    for (const displayGroup of licenseDisplayGroups) {
      displayGroup.subGroups = this.processLicenseSubGroupsWithCompatibilities(displayGroup.subGroups, forbiddenLicenseSelectors);
      newLicenseGroups.push(displayGroup);
    }

    return newLicenseGroups;
  }

  loadLicenseCategories = (licenseModel) => {
    this.updateStateIfMounted({licencesLoading: true, licenseDisplayGroups: []});
    getLicenceCategories(this.props.softwareSelection, licenseModel).then((response) => {
      const scopedLicences = {};
      const availableLicenseModel = {};

      for (const scopedLicense of (response.scopedLicenses || [])) {
        scopedLicences[scopedLicense.id] = true;
        if (scopedLicense.licenseModel) {
          availableLicenseModel[scopedLicense.licenseModel] = true;
        }
      }

      const displayGroups = this.processLicenseGroupsWithCompatibilites(response.licenseDisplayGroups);

      const scopedLicenseModels = (this.props.configuration.softwareInfo?.availableLicenseModels || []).filter(licenseModel => {
        return availableLicenseModel[licenseModel.applicationValue];
      });

      this.updateStateIfMounted({licenseDisplayGroups: displayGroups,
        scopedLicences, availableLicenseModel: scopedLicenseModels,
        mobileSelectedLicenseDisplayGroup: displayGroups?.[0]?.displayGroupKey
      });

      const knowMoreReferences = [];
      // Build the know more links
      for (const comparatorType of displayGroups.flatMap(displayGroup => (displayGroup.subGroups || [])).flatMap(subGroup => (subGroup.comparatorTypes || []))) {
        if (comparatorType.scopedKnowMoreReferences?.length > 0) {
          knowMoreReferences.push(comparatorType.scopedKnowMoreReferences[0].id);
        }
      }
      getKnowMoreLinks(knowMoreReferences).then((knowMoreLinksMap) => {
        this.updateStateIfMounted({knowMoreLinksMap});
      }).catch(() => {});

    }).finally(() => {
      this.updateStateIfMounted({licencesLoading: false});
    });
  }

  componentDidMount = () => {
    this._isMounted = true;
    this.loadLicenseCategories(this.props.licenseModel);
    this._selectedDisplayGroupChecker = setInterval(() => {
      if (this.state.licenseDisplayGroups?.length > 0) {
        let selectedDisplayGroup = this.state.licenseDisplayGroups[0];
        for (const displayGroup of this.state.licenseDisplayGroups) {
          const elt = document.getElementById(`plc-display-groups-${displayGroup.displayGroupKey}`);
          if (elt && (elt.getBoundingClientRect().top + 300) < window.innerHeight) {
            selectedDisplayGroup = displayGroup;
          }
        }

        if (selectedDisplayGroup?.displayGroupKey !== this.state.selectedDisplayGroup) {
          this.updateStateIfMounted({selectedDisplayGroup: selectedDisplayGroup.displayGroupKey});
        }
      }
    }, 200);
  }

  componentWillUnmount = () => {
    if (this._selectedDisplayGroupChecker) {
      clearInterval(this._selectedDisplayGroupChecker);
    }
    this._isMounted = false;
  }

  updateStateIfMounted(state) {
    if (this._isMounted) {
      this.setState(state);
    }
  }

  computeLicenseLevel = () => {
    let result;
    for (let i = 0; i < (this.props.softwareLicences || []).length && result == null ; i++) {
      const softwareLicence = this.props.softwareLicences[i];
      if (softwareLicence.licenseLevel != null) {
        result = softwareLicence.licenseLevel;
      }
    }

    return result;
  }

  openSelectorModal = (licenceType, startCriterias, scopedLicences) => {
    this.props.openModal({
      modalType: 'software-licence-selector',
      modalProps: {
        softwareLicences: this.props.softwareLicences,
        softwareSelection: this.props.softwareSelection,
        scopedLicences: scopedLicences,
        availableLicenseModel: this.state.availableLicenseModel,
        licenceType,
        startCriterias,
        licenseModel: this.props.licenseModel,
        licenseLevel: this.computeLicenseLevel(),
        onSubmit: (licencesToAdd, withNavigation) => {
          this.props.closeModal();
          this.props.addSoftwareLicenses(licencesToAdd, licenceType, withNavigation);
        },
        windowModeEnabled: true,
      }
    });
  }

  renderDisplayGroupTitle = (displayGroup, onClickCallback, selected) => {
    return (
      <DisplayGroupItem key={displayGroup.displayGroupKey} onClick={onClickCallback}>
        <DisplayGroupLabel $selected={selected}>
          {I10n.getOrDefault(`screen.licence_selector.display_group.name.${displayGroup.labelKey}`, displayGroup.displayGroupName)}
        </DisplayGroupLabel>
        <SelectionBar $selected={selected}></SelectionBar>
      </DisplayGroupItem>
    );
  }

  renderDisplayGroupHeaders = () => {
    return (<>
      <DesktopDisplayGroupTitlesWrapper>
        {this.state.licenseDisplayGroups.map((displayGroup) => {
          return (
            this.renderDisplayGroupTitle(displayGroup, () => {
              const element = document.getElementById(`plc-display-groups-${displayGroup.displayGroupKey}`);
              if (element == null) {
                return;
              }
              if (this._scrollComponent != null) {
                this._scrollComponent.scrollTo({
                  top:  element.offsetTop,
                  behavior: "smooth",
                });
              }
            }, this.state.selectedDisplayGroup === displayGroup.displayGroupKey));
        })}
      </DesktopDisplayGroupTitlesWrapper>
    <MobileDisplayGroupTitlesWrapper>
      {this.state.licenseDisplayGroups.map((displayGroup) => {
        return (
          this.renderDisplayGroupTitle(displayGroup, () => {
            this.updateStateIfMounted({mobileSelectedLicenseDisplayGroup: displayGroup.displayGroupKey});
          }, this.state.mobileSelectedLicenseDisplayGroup === displayGroup.displayGroupKey));
      })}
    </MobileDisplayGroupTitlesWrapper>
    </>);
  }

  renderNavigationButtonSection = () => {
    return (
      <NavigationButtonSection>
        <CancelWrapper>
          <Button
            id='license-selector-screen-back-button'
            disabled={this.props.configurationSaver.isSaving}
            onClick={event => {
              this.props.onPrevious();
            }}
          >
            {I10n.get('app.action.back')}
          </Button>
        </CancelWrapper>
        {this.props.softwareLicences?.length > 0 && <Button
          importance="emphasized"
          primary
          disabled={this.props.configurationSaver.isSaving}
          onClick={event => {
            this.props.onContinue();
          }}>
            {I10n.get('app.action.continue')}
        </Button>}
      </NavigationButtonSection>
    );
  }

  toggleLicenseModel(newLicenseModel) {
    if (this.props.softwareLicences.length > 0) {
      this.props.openModal({
        modalType: 'confirmation',
        modalProps: {
          onCancel: () => { this.props.closeModal(); },
          onSubmit: () => {
            this.props.closeModal();
            this.props.setLicenseModel(newLicenseModel);
            this.loadLicenseCategories(newLicenseModel);
          },
          title: I10n.get('screen.licence_selector.switch_license_model_modal_clear.title'),
          text: I10n.get('screen.licence_selector.switch_license_model_modal_clear.body'),
          cancelText: I10n.get('app.action.cancel'),
          submitText: I10n.get('app.action.continue'),
          windowModelEnabled: true
        }
      });
    } else {
      this.props.setLicenseModel(newLicenseModel)
      this.loadLicenseCategories(newLicenseModel);
    }
  }

  renderLicenseModelButtons = (size) => {
    return (
      <>
        {this.state.availableLicenseModel.map((licenseModel, idx) => {
          return (
            <LicenseModelButtonWrapper key={idx}>
              <Button
                id={`${licenseModel.applicationValue}-license-model-button`}
                size={size}
                disabled={this.props.configurationSaver.isSaving}
                importance={this.props.licenseModel === licenseModel.applicationValue ? 'emphasized' : 'standard'}
                onClick={(event) => {
                  event.preventDefault();
                  if (this.props.licenseModel !== licenseModel.applicationValue) {
                    this.toggleLicenseModel(licenseModel.applicationValue);
                  }
                }}
              >
                {I10n.get(`screen.licence_selector.license_model.${licenseModel.applicationValue}`)}
              </Button>

            </LicenseModelButtonWrapper>
          );
        })}
      </>
    );
  }

  renderLicenseModelToggle = () => {

    return (
      <LicenseModelWrapper>
        <MobileLicenseModelButtonsWrapper>
          {this.renderLicenseModelButtons('standard')}
        </MobileLicenseModelButtonsWrapper>
        <LicenseModelButtonsWrapper>
          {this.renderLicenseModelButtons('large')}
        </LicenseModelButtonsWrapper>
        <Link onClick={() => {
          this.props.openModal({
            modalType: 'compare-license-models',
            modalProps: {
              softwareSelection: this.props.softwareSelection
            }
          });
        }}>{I10n.get('screen.licence_selector.how_are_they_different')}</Link>
      </LicenseModelWrapper>
    );
  }

  render() {
    return (
      <>
        <TitleAndModelWrapper>
          <StepTitle>{I10n.get(`screen.licence_selector.${this.props.softwareSelection}.title`)}</StepTitle>
          {(window.top === window.self && this.state.availableLicenseModel.length > 1) && <>
            <StepSubtitle>{I10n.get('screen.licence_selector.subscription_mode.subtitle')}</StepSubtitle>
            {this.renderLicenseModelToggle()}
          </>}
          {this.renderDisplayGroupHeaders()}
        </TitleAndModelWrapper>
        <Container ref={(element) => {this._scrollComponent = element;}} id="slc-license-groups-scrollable-wrapper">
          <LicenceSelectorScreenV2Content
            softwareSelection={this.props.softwareSelection}
            licenseDisplayGroups={this.state.licenseDisplayGroups}
            onAddSoftwareLicencesRequested={(type) => {
              const startCriterias = type.startCriterias;

              const hardwareToSoftwareCompatibilities = this.props.hardwareToSoftwareCompatibilities;
              if (hardwareToSoftwareCompatibilities) {
                hardwareToSoftwareCompatibilities.forEach(compatibility => {
                  startCriterias.push(...(compatibility.startCriteria || []));
                });
              }

              this.openSelectorModal(type.licenseTypesKey, startCriterias, this.state.scopedLicences);
            }}
            knowMoreLinksMap={this.state.knowMoreLinksMap}
            licencesLoading={this.state.licencesLoading}
            mobileSelectedLicenseDisplayGroup={this.state.mobileSelectedLicenseDisplayGroup}
            isSaving={this.props.configurationSaver.isSaving}
          ></LicenceSelectorScreenV2Content>
        </Container>
        {this.renderNavigationButtonSection()}
      </>
    );
  }
}

const mapStateToProps = state => {
  return state;
};

const mapDispatchToProps = dispatch => {
  return {
    openModal: (modalTypeWithProps) => dispatch(openModal(modalTypeWithProps)),
    closeModal: () => dispatch(closeModal())
  };
};
export default connect(mapStateToProps, mapDispatchToProps)(LicenceSelectorScreenV2);
